import { buildResultUrl, zResultUrls, type ResultUrlsTypeEnum } from 'refresh-common'
import { z } from 'zod'
import { computed } from 'vue'
import { FORMS_OPTIN_PARAM, FORMS_OPTIN_VALUE } from '~/libs/formsDrupal'
import { omitPersonalParams } from '~/libs/resultDonor'
import type { ResultDocument } from '~/types/cms'
import type { Person } from '~/types/person'
import type { Company } from '~/types/company'

function isPerson(entity: Person | Company): entity is Person {
  return 'ssn' in entity
}

export const useResultUrls = ({
  entity,
  route,
  baseUrl,
  slug,
  type,
  resultDocuments,
  debug,
  wsId,
  partId,
  transactionId,
  webOrderId,
  customerId,
  invoiceId,
  campaignCode,
  form,
}: {
  entity: Ref<Person | Company>
  route: any
  baseUrl: string
  slug: string
  type: ResultUrlsTypeEnum
  resultDocuments: {
    ok: ResultDocument
    aborted?: ResultDocument
    postal?: ResultDocument
  }
  debug?: string
  wsId?: string
  partId?: string
  transactionId?: string
  webOrderId?: string
  customerId?: string
  invoiceId?: string
  campaignCode?: string
  /**
   * A Drupal form: the result page reads its own pages by id and language, so every state gets a
   * URL even without a Contentful entry, and an opted-in test stays opted in.
   */
  form?: { formId: string; formLang: string; optedIn: boolean }
}) => {
  type ResultUrlParamsInput = z.input<typeof zResultUrls>

  const resultReferences = {
    ok: resultDocuments.ok.sys.id,
    aborted: resultDocuments.aborted?.sys.id,
    postal: resultDocuments.postal?.sys.id,
  }

  const urls = computed(() => {
    const e = entity.value
    const rawQuery = route.query as Record<string, unknown>

    const commonParams: ResultUrlParamsInput = {
      ...omitPersonalParams(rawQuery),
      // Keep this key explicit because zResultUrls input expects it to exist.
      sum: rawQuery.sum,
      campaignCode,

      // Tracking params
      debug,
      slug,

      // Common params
      whId: isPerson(e) ? e.hashId : undefined,
      wsId,

      // Djurens Rätt params
      partId,
      transactionId,

      // 1882 Params
      webOrderId,
      customerId,
      invoiceId,

      ...(form ? { formId: form.formId, formLang: form.formLang } : {}),
    }
    const build = (params: ResultUrlParamsInput) => {
      const url = buildResultUrl(baseUrl, params)
      if (!form?.optedIn) return url
      const withOptIn = new URL(url)
      withOptIn.searchParams.set(FORMS_OPTIN_PARAM, FORMS_OPTIN_VALUE)
      return withOptIn.toString()
    }

    return {
      ok: build({
        ...commonParams,
        sysId: resultReferences.ok,
        type,
        state: 'ok',
      }),
      postal:
        resultReferences?.postal || form
          ? build({
              ...commonParams,
              sysId: resultReferences.postal,
              type,
              state: 'postal',
            })
          : undefined,
      aborted:
        resultReferences?.aborted || form
          ? build({
              ...commonParams,
              sysId: resultReferences.aborted,
              type,
              state: 'aborted',
            })
          : undefined,
    }
  })

  return urls
}
